import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../lib/api";
import { AdminNav } from "../components/AdminNav";
import { getAdminToken, adminLogout } from "../lib/adminAuth";
import { Loader2, Plus, Trash2, Save, Upload, Headphones, Feather, EyeOff, Eye, Mail, Check } from "lucide-react";

const SECTIONS = [
  { value: "reframe", label: "Somatic Reframe", icon: Feather },
  { value: "reflection", label: "Reflection / Audio", icon: Headphones },
];

const blank = () => ({ section: "reframe", title: "", body: "", month: "", is_published: true, order: 0 });

export default function AdminVault() {
  const [token] = useState(() => getAdminToken());
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null); // selected entry id or "new"
  const [form, setForm] = useState(blank());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const fileRef = useRef(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/vault`, auth);
      setRows(data.entries || []);
    } catch (e) {
      if (e.response?.status === 401) { adminLogout(); return; }
      setErr("Could not load Vault entries.");
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const openEntry = (r) => { setSel(r.id); setForm({ ...r }); setMsg(""); setErr(""); };
  const openNew = () => { setSel("new"); setForm(blank()); setMsg(""); setErr(""); };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true); setErr(""); setMsg("");
    const body = {
      section: form.section, title: form.title, body: form.body,
      month: form.month, is_published: form.is_published, order: Number(form.order) || 0,
    };
    try {
      if (sel === "new") {
        const { data } = await axios.post(`${API}/admin/vault`, body, auth);
        setRows((r) => [...r, data]);
        setSel(data.id); setForm({ ...data });
        setMsg(data.notified_at ? "Saved — Vault members are being notified." : "Saved as a draft. Members are notified when you publish.");
        return;
      } else {
        await axios.patch(`${API}/admin/vault/${sel}`, body, auth);
        setRows((r) => r.map((x) => (x.id === sel ? { ...x, ...body } : x)));
      }
      setMsg("Saved.");
    } catch (e) {
      if (e.response?.status === 401) { adminLogout(); return; }
      setErr(e.response?.data?.detail || "Could not save. Please try again.");
    } finally { setSaving(false); }
  };

  const notifyMembers = async () => {
    if (sel === "new") { setErr("Save the entry first, then notify members."); return; }
    if (!window.confirm("Email all Language Vault members that this entry is new?")) return;
    setNotifying(true); setErr(""); setMsg("");
    try {
      const { data } = await axios.post(`${API}/admin/vault/${sel}/notify`, {}, auth);
      setRows((r) => r.map((x) => (x.id === sel ? { ...x, notified_at: new Date().toISOString() } : x)));
      setForm((f) => ({ ...f, notified_at: new Date().toISOString() }));
      setMsg(`Notified ${data.sent} of ${data.members} member${data.members === 1 ? "" : "s"}.`);
    } catch (e) {
      if (e.response?.status === 401) { adminLogout(); return; }
      setErr(e.response?.data?.detail || "Could not send notifications.");
    } finally { setNotifying(false); }
  };

  const remove = async () => {
    if (sel === "new") { setSel(null); return; }
    if (!window.confirm("Delete this Vault entry? Members will no longer see it.")) return;
    try {
      await axios.delete(`${API}/admin/vault/${sel}`, auth);
      setRows((r) => r.filter((x) => x.id !== sel));
      setSel(null); setForm(blank());
    } catch (e) {
      if (e.response?.status === 401) { adminLogout(); return; }
      setErr("Could not delete.");
    }
  };

  const uploadAudio = async (file) => {
    if (!file || sel === "new") { setErr("Save the entry first, then add audio."); return; }
    setUploading(true); setErr(""); setMsg("");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/admin/vault/${sel}/audio`, fd, {
        headers: { ...auth.headers, "Content-Type": "multipart/form-data" },
      });
      setRows((r) => r.map((x) => (x.id === sel ? { ...x, has_audio: true, audio_filename: data.audio_filename } : x)));
      setForm((f) => ({ ...f, has_audio: true, audio_filename: data.audio_filename }));
      setMsg("Audio uploaded.");
    } catch (e) {
      if (e.response?.status === 401) { adminLogout(); return; }
      setErr(e.response?.data?.detail || "Audio upload failed.");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="min-h-[100dvh] bg-ivory-100" data-testid="admin-vault">
      <div className="mx-auto max-w-6xl px-5 pt-8 sm:px-8">
        <AdminNav onLogout={adminLogout} />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl text-ink">The Language Vault</h1>
            <p className="mt-1 text-sm text-ink-soft">Monthly reframes, reflections and audio your members see inside <code className="rounded bg-ink/5 px-1">/vault</code>.</p>
          </div>
          <button onClick={openNew} data-testid="vault-new-entry" className="debossed-btn inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-display text-ink">
            <Plus className="h-4 w-4" /> New entry
          </button>
        </div>
      </div>

      <div className="mx-auto grid max-w-6xl gap-6 px-5 py-8 sm:grid-cols-[320px_1fr] sm:px-8">
        <div className="space-y-2">
          {loading && <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-ink-muted" /></div>}
          {!loading && rows.length === 0 && (
            <div className="rounded-xl bg-ivory-50 p-6 text-center ring-1 ring-ivory-400/50">
              <p className="text-sm text-ink-muted">No entries yet. Create your first reframe or reflection.</p>
            </div>
          )}
          {rows.map((r) => {
            const Icon = SECTIONS.find((s) => s.value === r.section)?.icon || Feather;
            return (
              <button key={r.id} onClick={() => openEntry(r)} data-testid={`vault-row-${r.id}`}
                className={`block w-full rounded-xl p-3 text-left ring-1 transition ${sel === r.id ? "bg-gold/10 ring-gold/40" : "bg-ivory-50 ring-ivory-400/50 hover:ring-gold/30"}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-1.5 truncate font-display text-ink"><Icon className="h-3.5 w-3.5 text-gold-deep" /> {r.title || "Untitled"}</span>
                  {!r.is_published && <EyeOff className="h-3.5 w-3.5 flex-none text-ink-muted" />}
                </div>
                <p className="mt-0.5 truncate text-xs text-ink-muted">{r.month || "—"}{r.has_audio ? " · audio" : ""} · #{r.order}</p>
              </button>
            );
          })}
        </div>

        <div>
          {!sel ? (
            <p className="text-sm text-ink-muted">Select an entry to edit, or create a new one.</p>
          ) : (
            <div className="rounded-2xl bg-ivory-50 p-6 ring-1 ring-ivory-400/50 sm:p-7" data-testid="vault-editor">
              <div className="flex flex-wrap items-center gap-2">
                {SECTIONS.map((s) => (
                  <button key={s.value} onClick={() => setField("section", s.value)} data-testid={`vault-section-${s.value}`}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs ${form.section === s.value ? "bg-ink text-ivory-50" : "border border-ink/20 text-ink-soft"}`}>
                    <s.icon className="h-3.5 w-3.5" /> {s.label}
                  </button>
                ))}
                <button onClick={() => setField("is_published", !form.is_published)} data-testid="vault-publish-toggle"
                  className={`ml-auto inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs ${form.is_published ? "bg-sage/25 text-ink" : "border border-ink/20 text-ink-soft"}`}>
                  {form.is_published ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />} {form.is_published ? "Published" : "Hidden"}
                </button>
              </div>

              <div className="mt-5 grid gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <label className="text-sm text-ink-soft">Month / label
                    <input value={form.month || ""} onChange={(e) => setField("month", e.target.value)} data-testid="vault-field-month" placeholder="e.g. June 2026"
                      className="mt-1 w-full rounded-lg border border-ink/15 bg-white px-3 py-2 text-ink" />
                  </label>
                  <label className="text-sm text-ink-soft">Order
                    <input type="number" value={form.order ?? 0} onChange={(e) => setField("order", e.target.value)} data-testid="vault-field-order"
                      className="mt-1 w-full rounded-lg border border-ink/15 bg-white px-3 py-2 text-ink" />
                  </label>
                </div>
                <label className="text-sm text-ink-soft">Title
                  <input value={form.title || ""} onChange={(e) => setField("title", e.target.value)} data-testid="vault-field-title"
                    className="mt-1 w-full rounded-lg border border-ink/15 bg-white px-3 py-2 font-display text-ink" />
                </label>
                <label className="text-sm text-ink-soft">Body
                  <textarea value={form.body || ""} onChange={(e) => setField("body", e.target.value)} data-testid="vault-field-body" rows={6}
                    className="mt-1 w-full rounded-lg border border-ink/15 bg-white px-3 py-2 text-ink" />
                </label>
              </div>

              <div className="mt-5 rounded-xl bg-ivory-100 p-4 ring-1 ring-ivory-400/50">
                <p className="flex items-center gap-2 text-sm text-ink"><Headphones className="h-4 w-4 text-gold-deep" /> Audio recording {form.has_audio && <span className="text-xs text-ink-muted">· {form.audio_filename}</span>}</p>
                <input ref={fileRef} type="file" accept="audio/*" data-testid="vault-audio-input"
                  onChange={(e) => uploadAudio(e.target.files?.[0])} className="mt-2 block w-full text-sm text-ink-soft file:mr-3 file:rounded-full file:border-0 file:bg-ink file:px-4 file:py-2 file:text-ivory-50" />
                {sel === "new" && <p className="mt-2 text-xs text-ink-muted">Save the entry first, then attach audio.</p>}
                {uploading && <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-ink-soft"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading…</p>}
              </div>

              {err && <p className="mt-4 text-sm text-terracotta" data-testid="vault-editor-error">{err}</p>}
              {msg && <p className="mt-4 text-sm text-gold-deep" data-testid="vault-editor-msg">{msg}</p>}

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button onClick={save} disabled={saving} data-testid="vault-save" className="debossed-btn inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-display text-ink disabled:opacity-50">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
                </button>
                {sel !== "new" && (
                  <button onClick={notifyMembers} disabled={notifying} data-testid="vault-notify-members"
                    title="Email all Language Vault members that this entry is new"
                    className="inline-flex items-center gap-2 rounded-full border border-gold/40 px-4 py-2.5 font-display text-sm text-gold-deep disabled:opacity-50">
                    {notifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Notify members
                  </button>
                )}
                <button onClick={remove} data-testid="vault-delete" className="inline-flex items-center gap-2 rounded-full border border-terracotta/40 px-4 py-2.5 font-display text-sm text-terracotta">
                  <Trash2 className="h-4 w-4" /> {sel === "new" ? "Cancel" : "Delete"}
                </button>
                {sel !== "new" && form.notified_at && (
                  <span className="inline-flex items-center gap-1.5 text-xs text-ink-muted" data-testid="vault-notified-status">
                    <Check className="h-3.5 w-3.5 text-sage" /> Members notified {new Date(form.notified_at).toLocaleDateString()}
                  </span>
                )}
              </div>
              <p className="mt-3 text-xs text-ink-muted">New published entries notify Vault members automatically. Use "Notify members" to resend.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
